import { get } from 'lodash';
import getConfig from 'next/config';
import * as Sentry from '@sentry/nextjs';
import LocalStorage from '../../lib/storage/local';
import MemoryStorage from '../../lib/storage/memory';
import { fetchTokenFromAzure } from '../../lib/utils';
import { ME_QUERY, MUTATION_LOGIN, MUTATION_CHANGE_ACTIVE_ORGANIZATION } from './gql';

export const createAuthStore = () => {
  let store;
  const initial = { token: null, expiry: null, authError: null };
  if (!process.browser) {
    store = new MemoryStorage(initial);
  } else {
    store = new LocalStorage('LLV2_GQL_AUTH', initial);
  }

  // need to check if any tokens we had are expired.
  const expiry = store.get('expiry', null);
  if (expiry) {
    const rightNow = Math.floor(Date.now() / 1000);
    const isExpired = rightNow >= expiry;
    if (isExpired) {
      /* eslint-disable-next-line */
      console.warn('TOKEN IS EXPIRED, PURGING VALUES', rightNow, expiry);
      store.set('token', null);
      store.set('expiry', null);
      store.set('authError', null);

      // Clear Sentry user context when token expires
      if (process.browser) {
        Sentry.setUser(null);
      }
    }
  }

  return store;
};

let AUTH_STORE = null;
export const getAuthStore = (recreate = false) => {
  if (!AUTH_STORE || recreate) AUTH_STORE = createAuthStore();
  return AUTH_STORE;
};

export const defaultAuthState = {
  authError: null
};

export const setAuthTokens = async (client, store, variables) => {
  const { token = null, expiry = null, authError = null } = variables;
  store.set('token', token);
  store.set('expiry', expiry);
  store.set('authError', authError);
};

// Fetch a new auth token from the AUTH_HUB service
export const getAuthToken = async () => {
  const { accessToken = null } = await fetchTokenFromAzure();
  return accessToken;
};

export const GQLLogin = async (client, store, variables) => {
  let errorMessage = null;
  let accountToken = '';
  let tokenExpiry = '';

  try {
    const { data, errors } = await client.mutate({
      errorPolicy: 'all',
      mutation: MUTATION_LOGIN,
      variables,

      update: (proxy, response) => {
        const me = get(response, 'data.login.me', null);
        proxy.writeQuery({
          query: ME_QUERY,
          data: {
            me
          }
        });
      }
    });

    if (errors && errors.length > 0) {
      errorMessage = get(errors, '[0].data.errorMessage', 'Unknown authentication error.');
    }
    const message = get(errors, '0.message', 'An unknown authentication error occurred.');
    const isLodgeLinkAdmin = get(data, 'login.me.admin', null);
    if (isLodgeLinkAdmin) {
      accountToken = get(data, 'login.accountToken', '');
      tokenExpiry = get(data, 'login.tokenExpiry', '');
    } else {
      errorMessage = 'You do not have permission to sign in to this portal';
    }
    if (message.includes('Wrong email')) {
      errorMessage = 'Wrong email or password';
    }
    if (message === 'This operation requires the user to be active') {
      errorMessage =
        'Your account has been deactivated. Please contact your Organization Admin or LodgeLink Support for assistance.';
    }
  } catch (error) {
    console.log(`Authentication Error:`, error);
    errorMessage = 'Unknown authentication error.';
  }

  const tokenVars = { token: accountToken, expiry: tokenExpiry, authError: errorMessage };
  await setAuthTokens(client, store, tokenVars);
  return errorMessage;
};

export const GQLLogout = async (client, store) => {
  const {
    publicRuntimeConfig: { AUTH_HUB_URL, LODGELINK_REDIRECT_URL }
  } = getConfig();
  const tokenVars = { token: null, expiry: null, authError: null };
  await setAuthTokens(client, store, tokenVars);

  // add any extra clean up tasks here as they become required over time.
  try {
    if (process.browser) {
      window.localStorage.clear();
      const redirectParam = `/logout?returnTo=${encodeURIComponent(LODGELINK_REDIRECT_URL)}`;
      window.location.replace(`${AUTH_HUB_URL}${redirectParam}`);
    }
  } catch (error) {
    /* eslint-disable-next-line */
    console.log('Error clearing logout session information.', error);
  }
};

export const GQLChangeOrganization = async (client, store, variables) => {
  try {
    await client.mutate({
      errorPolicy: 'all',
      mutation: MUTATION_CHANGE_ACTIVE_ORGANIZATION,
      variables,
      update: (proxy, response) => {
        const me = get(response, 'data.changeActiveOrganization.me', null);
        proxy.writeQuery({
          query: ME_QUERY,
          data: {
            me
          }
        });
      }
    });
  } catch (error) {
    /* eslint-disable */
    console.log('MUTATION_CHANGE_ACTIVE_ORGANIZATION');
    console.log({ error });
    console.log({ variables });
    /* eslint-enable */
    await GQLLogout(client, store);
  }
};

export const createVersionInfoStore = () => {
  let store;
  const VERSION_INFO_STORE_KEY = 'LLV2_VERSION_INFO';

  if (!process.browser) {
    store = new MemoryStorage({});
  } else {
    store = new LocalStorage(VERSION_INFO_STORE_KEY, {});
  }

  return store;
};

let VERSION_INFO_STORE = null;
export const getVersionInfoStore = (recreate = false) => {
  if (!VERSION_INFO_STORE || recreate) {
    VERSION_INFO_STORE = createVersionInfoStore();
  }
  return VERSION_INFO_STORE;
};
